export interface TagChipStyle {
  borderColor: string;
  backgroundColor: string;
  /** Texto no tema claro */
  color: string;
  /** Texto no tema escuro (fallback: color) */
  colorDark?: string;
}

export interface TagFiltroChip {
  tag: string;
  label: string;
  count: number;
  estilo: TagChipStyle;
}

/** Tags oficiais enviadas pelo Rabbit (valor exato ou alias legado). */
type TagCanonica =
  | 'SEM_PENDENCIA'
  | 'ICMS_DECLARADO'
  | 'ICMS_PARCELAMENTO'
  | 'IPVA'
  | 'ITCMD'
  | 'AIIM'
  | 'ICMS_PENDENCIA'
  | 'TRANSPORTE_SALDO_CREDOR'
  | 'OMISSO_DECLARACAO'
  | 'PENDENCIAS'
  | 'PENDENCIA_INSCRITA'
  | 'SEM_DEBITOS'
  | 'TEM_DEBITOS';

interface TagDefinicao {
  label: string;
  estilo: TagChipStyle;
  ordem: number;
}

const ESTILO_VERDE: TagChipStyle = {
  borderColor: '#0f8b5f',
  backgroundColor: 'rgba(15, 139, 95, 0.14)',
  color: '#0a6b48',
  colorDark: '#c8f0df'
};

const ESTILO_AMARELO: TagChipStyle = {
  borderColor: '#c49200',
  backgroundColor: 'rgba(196, 146, 0, 0.16)',
  color: '#8a6800',
  colorDark: '#f8e7b0'
};

const ESTILO_VERMELHO: TagChipStyle = {
  borderColor: '#b83232',
  backgroundColor: 'rgba(184, 50, 50, 0.14)',
  color: '#9a2828',
  colorDark: '#f5c4c4'
};

const ESTILO_AZUL: TagChipStyle = {
  borderColor: '#2f6fed',
  backgroundColor: 'rgba(47, 111, 237, 0.12)',
  color: '#1f54c4',
  colorDark: '#d4defb'
};

const ESTILO_LARANJA: TagChipStyle = {
  borderColor: '#d35400',
  backgroundColor: 'rgba(211, 84, 0, 0.14)',
  color: '#a84300',
  colorDark: '#fde2c8'
};

const ESTILO_ROXO: TagChipStyle = {
  borderColor: '#8e44ad',
  backgroundColor: 'rgba(142, 68, 173, 0.12)',
  color: '#6c3483',
  colorDark: '#e8d4f0'
};

const ESTILO_INDIGO: TagChipStyle = {
  borderColor: '#5b6abf',
  backgroundColor: 'rgba(91, 106, 191, 0.12)',
  color: '#3f4d99',
  colorDark: '#d8dcf5'
};

const ESTILO_CINZA: TagChipStyle = {
  borderColor: '#5c6770',
  backgroundColor: 'rgba(92, 103, 112, 0.12)',
  color: '#3d464d',
  colorDark: '#e2e6ea'
};

const TAG_DEFINICOES: Record<TagCanonica, TagDefinicao> = {
  SEM_PENDENCIA: { label: 'Sem pendência', estilo: ESTILO_VERDE, ordem: 90 },
  ICMS_DECLARADO: { label: 'ICMS Declarado', estilo: ESTILO_AZUL, ordem: 50 },
  ICMS_PARCELAMENTO: { label: 'ICMS Parcelamento', estilo: ESTILO_LARANJA, ordem: 40 },
  IPVA: { label: 'IPVA', estilo: ESTILO_ROXO, ordem: 30 },
  ITCMD: { label: 'ITCMD', estilo: ESTILO_INDIGO, ordem: 20 },
  AIIM: { label: 'AIIM', estilo: ESTILO_CINZA, ordem: 10 },
  ICMS_PENDENCIA: { label: 'ICMS Pendência', estilo: ESTILO_VERMELHO, ordem: 80 },
  TRANSPORTE_SALDO_CREDOR: {
    label: 'Transporte de Saldo Credor',
    estilo: ESTILO_VERMELHO,
    ordem: 70
  },
  OMISSO_DECLARACAO: { label: 'Omisso de Declaração', estilo: ESTILO_VERMELHO, ordem: 60 },
  PENDENCIAS: { label: 'Pendências', estilo: ESTILO_AMARELO, ordem: 75 },
  PENDENCIA_INSCRITA: { label: 'Pendência inscrita', estilo: ESTILO_AMARELO, ordem: 95 },
  SEM_DEBITOS: { label: 'Sem débitos', estilo: ESTILO_VERDE, ordem: 85 },
  TEM_DEBITOS: { label: 'Tem débitos', estilo: ESTILO_VERMELHO, ordem: 80 }
};

const PALETA_FALLBACK: TagChipStyle[] = [
  ESTILO_AZUL,
  ESTILO_ROXO,
  ESTILO_INDIGO,
  ESTILO_LARANJA
];

/** Mapeia chave normalizada (sem acento, upper, _ → espaço) para tag canônica. */
const TAG_ALIASES: Record<string, TagCanonica> = {
  'SEM PENDENCIA': 'SEM_PENDENCIA',
  SEM_PENDENCIA: 'SEM_PENDENCIA',
  'SEM PENDENCIAS': 'SEM_PENDENCIA',
  SEM_PENDENCIAS: 'SEM_PENDENCIA',

  'ICMS DECLARADO': 'ICMS_DECLARADO',
  ICMS_DECLARADO: 'ICMS_DECLARADO',

  'ICMS PARCELAMENTO': 'ICMS_PARCELAMENTO',
  ICMS_PARCELAMENTO: 'ICMS_PARCELAMENTO',

  IPVA: 'IPVA',

  ITCMD: 'ITCMD',

  AIIM: 'AIIM',

  'ICMS PENDENCIA': 'ICMS_PENDENCIA',
  ICMS_PENDENCIA: 'ICMS_PENDENCIA',

  'TRANSPORTE DE SALDO CREDOR': 'TRANSPORTE_SALDO_CREDOR',
  TRANSPORTE_SALDO_CREDOR: 'TRANSPORTE_SALDO_CREDOR',

  'OMISSO DE DECLARACAO': 'OMISSO_DECLARACAO',
  OMISSO_DECLARACAO: 'OMISSO_DECLARACAO',

  PENDENCIAS: 'PENDENCIAS',
  PENDÊNCIAS: 'PENDENCIAS',

  // Pendência inscrita
  'PENDENCIA INSCRITA': 'PENDENCIA_INSCRITA',
  PENDENCIA_INSCRITA: 'PENDENCIA_INSCRITA',
  'PENDENCIAS INSCRITAS': 'PENDENCIA_INSCRITA',
  PENDENCIAS_INSCRITAS: 'PENDENCIA_INSCRITA',

  'SEM DEBITOS': 'SEM_DEBITOS',
  SEM_DEBITOS: 'SEM_DEBITOS',
  'SEM DEBITO': 'SEM_DEBITOS',
  SEM_DEBITO: 'SEM_DEBITOS',
  'NAO HA DEBITOS': 'SEM_DEBITOS',
  'NAO HA DEBITO': 'SEM_DEBITOS',

  'TEM DEBITOS': 'TEM_DEBITOS',
  TEM_DEBITOS: 'TEM_DEBITOS',
  'TEM DEBITO': 'TEM_DEBITOS',
  TEM_DEBITO: 'TEM_DEBITOS',
  'HA DEBITOS': 'TEM_DEBITOS',
  'HA DEBITO': 'TEM_DEBITOS',

  // Legado
  COMPROMETIDO: 'TRANSPORTE_SALDO_CREDOR',
  'TEM PENDENCIAS': 'PENDENCIAS',
  TEM_PENDENCIAS: 'PENDENCIAS'
};

export function normalizarTagChave(tag: string): string {
  return canonicalizarTag(tag);
}

export function normalizarConjuntoTags(tags: string[]): string[] {
  return [...new Set(tags.map((tag) => canonicalizarTag(tag)).filter(Boolean))].sort();
}

function canonicalizarTag(tag: string): string {
  return tag
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function resolverTagCanonica(tag: string): TagCanonica | null {
  const chave = canonicalizarTag(tag);
  return TAG_ALIASES[chave] ?? TAG_ALIASES[chave.replace(/ /g, '_')] ?? null;
}

export function formatarLabelTag(tag: string): string {
  const canonica = resolverTagCanonica(tag);
  if (canonica) return TAG_DEFINICOES[canonica].label;
  if (/[\s-]/.test(tag.trim())) return tag.trim();
  return tag
    .trim()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letra) => letra.toUpperCase());
}

export function resolverEstiloTag(tag: string, indiceFallback: number): TagChipStyle {
  const canonica = resolverTagCanonica(tag);
  if (canonica) return TAG_DEFINICOES[canonica].estilo;
  return PALETA_FALLBACK[indiceFallback % PALETA_FALLBACK.length];
}

/** Aplica estilo da tag respeitando tema claro/escuro do body. */
export function estiloChipNgStyle(estilo: TagChipStyle): Record<string, string> {
  const isDark =
    typeof document !== 'undefined' &&
    document.body.classList.contains('dark-theme');

  return {
    'border-color': estilo.borderColor,
    'background-color': estilo.backgroundColor,
    color: isDark ? estilo.colorDark ?? estilo.color : estilo.color
  };
}

function ordemTag(tag: string): number {
  const canonica = resolverTagCanonica(tag);
  if (canonica) return TAG_DEFINICOES[canonica].ordem;
  return -1;
}

export function ordenarTags(tags: string[]): string[] {
  return [...tags].sort((a, b) => {
    const oa = ordemTag(a);
    const ob = ordemTag(b);
    if (oa >= 0 && ob >= 0) return ob - oa;
    if (oa >= 0) return -1;
    if (ob >= 0) return 1;
    return formatarLabelTag(a).localeCompare(formatarLabelTag(b), 'pt-BR');
  });
}

export function montarFiltrosTags(
  processos: Array<{ tags: string[] }>,
  filtrosAtivosAnteriores: Record<string, boolean>,
  opcoes?: { apenasTagsInscrita?: boolean }
): { chips: TagFiltroChip[]; filtrosAtivos: Record<string, boolean> } {
  const contagem = new Map<string, number>();

  for (const processo of processos) {
    const tagsUnicas = new Set(
      processo.tags.map((tag) => tag.trim()).filter(Boolean)
    );

    for (const tag of tagsUnicas) {
      if (opcoes?.apenasTagsInscrita && !isTagFiltroInscrita(tag)) continue;
      contagem.set(tag, (contagem.get(tag) ?? 0) + 1);
    }
  }

  const tagsOrdenadas = ordenarTags(Array.from(contagem.keys()));
  const filtrosAtivos: Record<string, boolean> = {};
  const chips: TagFiltroChip[] = [];
  let indiceFallback = 0;

  for (const tag of tagsOrdenadas) {
    filtrosAtivos[tag] = filtrosAtivosAnteriores[tag] ?? true;
    chips.push({
      tag,
      label: formatarLabelTag(tag),
      count: contagem.get(tag) ?? 0,
      estilo: resolverEstiloTag(tag, indiceFallback++)
    });
  }

  return { chips, filtrosAtivos };
}

/** Tags oficiais para filtro no relatório de pendência não inscrita (tela RPA). */
export const RELATORIO_TAG_FILTER_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'ICMS Declarado', value: 'ICMS Declarado' },
  { label: 'ICMS Parcelamento', value: 'ICMS Parcelamento' },
  { label: 'IPVA', value: 'IPVA' },
  { label: 'ITCMD', value: 'ITCMD' },
  { label: 'AIIM', value: 'AIIM' },
  { label: 'ICMS Pendência', value: 'ICMS Pendência' },
  { label: 'Omisso de Declaração', value: 'Omisso de Declaração' },
  { label: 'Transporte de Saldo Credor', value: 'Transporte de Saldo Credor' },
  { label: 'Pendências', value: 'Pendências' },
  { label: 'Sem pendência', value: 'SEM_PENDENCIA' }
];

/** Tags oficiais para filtro no relatório / análise de pendência inscrita. */
export const RELATORIO_TAG_FILTER_OPTIONS_INSCRITA: Array<{ label: string; value: string }> = [
  { label: 'Sem débitos', value: 'SEM_DEBITOS' },
  { label: 'Tem débitos', value: 'TEM_DEBITOS' }
];

const TAGS_FILTRO_INSCRITA = new Set<TagCanonica>(['SEM_DEBITOS', 'TEM_DEBITOS']);

export function isTagFiltroInscrita(tag: string): boolean {
  if (isTagCnpj(tag)) return false;
  const canonica = resolverTagCanonica(tag);
  return canonica != null && TAGS_FILTRO_INSCRITA.has(canonica);
}

/** CNPJ (rótulo ou número) não entra no filtro de pendência inscrita. */
export function isTagCnpj(tag: string): boolean {
  const chave = canonicalizarTag(tag);
  if (chave === 'CNPJ' || chave === 'CNPJ CPF' || chave === 'CNPJ/CPF') return true;
  const soDigitos = tag.replace(/\D/g, '');
  return soDigitos.length === 14;
}

export function montarFiltrosTagsEstaticos(
  opcoes: Array<{ label: string; value: string }>,
  filtrosAtivosAnteriores: Record<string, boolean> = {}
): { chips: TagFiltroChip[]; filtrosAtivos: Record<string, boolean> } {
  const filtrosAtivos: Record<string, boolean> = {};
  const chips: TagFiltroChip[] = [];
  let indiceFallback = 0;

  for (const tag of ordenarTags(opcoes.map((opcao) => opcao.value))) {
    const opcao = opcoes.find((item) => item.value === tag);
    if (!opcao) continue;

    filtrosAtivos[tag] = filtrosAtivosAnteriores[tag] ?? true;
    chips.push({
      tag,
      label: opcao.label,
      count: 0,
      estilo: resolverEstiloTag(tag, indiceFallback++)
    });
  }

  return { chips, filtrosAtivos };
}

/**
 * Mesma regra da lista de análise de pendência (não inscrita):
 * - todos os chips ativos → sem filtro;
 * - 1 chip ativo → somente registros com exclusivamente aquela tag;
 * - vários chips ativos → tag única no registro entra se estiver ativa;
 *   várias tags no registro → todas devem estar entre os chips ativos.
 */
export function filtrarPorTagsAnalise(
  itemTags: string[],
  chips: TagFiltroChip[],
  filtrosAtivos: Record<string, boolean>
): boolean {
  if (chips.length === 0) return true;

  const tagsAtivas = chips
    .filter((chip) => filtrosAtivos[chip.tag])
    .map((chip) => chip.tag);

  if (tagsAtivas.length === 0) return false;
  if (tagsAtivas.length === chips.length) return true;

  const conjuntoItem = normalizarConjuntoTags(itemTags);
  if (conjuntoItem.length === 0) return false;

  const tagsAtivasNormalizadas = normalizarConjuntoTags(tagsAtivas);
  const tagsAtivasSet = new Set(tagsAtivasNormalizadas);

  if (tagsAtivas.length === 1) {
    return conjuntoItem.length === 1 && conjuntoItem[0] === tagsAtivasNormalizadas[0];
  }

  return conjuntoItem.every((tag) => tagsAtivasSet.has(tag));
}

/**
 * Filtro da pendência inscrita: documentos costumam ter "Pendência inscrita" +
 * "Sem débitos" ou "Tem débitos". Usa contém (OR) — basta o item ter ao menos
 * uma das tags ativas.
 */
export function filtrarPorTagsInscrita(
  itemTags: string[],
  chips: TagFiltroChip[],
  filtrosAtivos: Record<string, boolean>
): boolean {
  if (chips.length === 0) return true;

  const tagsAtivas = chips
    .filter((chip) => filtrosAtivos[chip.tag])
    .map((chip) => chip.tag);

  if (tagsAtivas.length === 0) return false;
  if (tagsAtivas.length === chips.length) return true;

  const tagsItemFiltro = (itemTags ?? [])
    .map((tag) => String(tag ?? '').trim())
    .filter((tag) => tag && isTagFiltroInscrita(tag));

  if (tagsItemFiltro.length === 0) return false;

  const conjuntoItem = normalizarConjuntoTags(tagsItemFiltro);
  const tagsAtivasSet = new Set(normalizarConjuntoTags(tagsAtivas));

  return conjuntoItem.some((tag) => tagsAtivasSet.has(tag));
}

/** Tags ativas para query string da API. Undefined = todos os chips ativos (sem filtro). */
export function resolverTagsParaFiltroApi(
  chips: TagFiltroChip[],
  filtrosAtivos: Record<string, boolean>
): string[] | undefined {
  if (chips.length === 0) return undefined;

  const tagsSelecionadas = chips
    .filter((chip) => filtrosAtivos[chip.tag])
    .map((chip) => chip.tag);

  if (tagsSelecionadas.length === 0) return undefined;
  if (tagsSelecionadas.length === chips.length) return undefined;

  return tagsSelecionadas;
}

export function normalizarCompetencia(competencia: string): string {
  const texto = String(competencia ?? '').trim();
  const match = texto.match(/^(\d{1,2})-(\d{4})$/);
  if (!match) return texto.toUpperCase();
  return `${match[1].padStart(2, '0')}-${match[2]}`;
}

export function competenciaCoincide(competenciaItem: string, competenciaFiltro: string): boolean {
  return normalizarCompetencia(competenciaItem) === normalizarCompetencia(competenciaFiltro);
}
